// "Most" (roadmap 3.2), in the design system's order: greeting + core → the one next step → decisions →
// lens tiles → today → hot threads (with their hot sub-threads) → tasks → the rest.
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, ft, HeatRing, JarvisCore, Label, Panel, tc } from '../ui';
import { PlacementCard } from './PlacementCard';

interface T { id: string; name: string; parent_id: string | null; temperature: string; own_temperature: string; heat: number; next_step: string | null; next_deadline: string | null; wake_reason: string | null; focus: boolean; overdue: boolean; net: number | null }
interface Data {
  replies: Array<{ event_id: string; occurred_at: string; from_name: string | null; from_email: string; subject: string | null; question: string | null; days: number }>;
  next: { kind: 'task' | 'thread'; ref?: string; id?: string; title: string; why: string; due: string | null } | null;
  decisions: Array<{ event_id: string; thread_id: string; thread: string; parent: string | null; confidence: number; occurred_at: string; kind: string; text: string }>;
  decisionCount: number;
  money: { free: number | null; perDay: number | null; state: string; balance: number | null; balanceAt: string | null; spentCycle: number; daysToPayday: number; payday: string };
  uni: { next_exam: string | null; exam_subject: string | null; next_class: string | null; overdue: number };
  today: Array<{ id: string; at: string; kind: string; subject: string | null; location: string | null; text: string }>;
  tasks: Array<{ ref: string; title: string; due: string | null; thread: string | null; overdue: boolean }>;
  threads: T[];
}

const greeting = () => { const h = new Date().getHours(); return h < 5 ? 'Jó éjszakát' : h < 10 ? 'Jó reggelt' : h < 18 ? 'Szia' : 'Jó estét'; };
const time = (iso: string) => new Date(iso).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
const KIND: Record<string, string> = { payment: 'fizetés', message: 'üzenet', task: 'teendő', exam: 'vizsga', plan: 'terv' };

export function Now({ compact = false }: { compact?: boolean }) {
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  const [gone, setGone] = useState<Set<string>>(new Set());
  const load = useCallback(() => api<Data>('/api/now').then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); return onChange(load); }, [load]);
  const act = (p: Promise<unknown>) => p.then(load).catch((e) => setErr(e.message));
  const task = (ref: string, action: string, days?: number) => act(api(`/api/tasks/${ref}`, { action, days }));
  const hide = (k: string, on: boolean) => setGone((g) => { const n = new Set(g); on ? n.add(k) : n.delete(k); return n; });
  const decide = (x: Data['decisions'][number], action: 'ok' | 'none') => { const k = x.event_id + x.thread_id; hide(k, true);
    api(`/api/decisions/${x.event_id}`, { thread: x.thread_id, action }).then(load).catch((e) => { hide(k, false); setErr(e.message); }); };

  if (err && !d) return <p className="err">{err}</p>;
  if (!d) return <p className="muted">Betöltés…</p>;

  const top = d.threads.filter((t) => !t.parent_id);
  const hot = top.filter((t) => t.temperature === 'forró');
  const hotKids = (id: string) => d.threads.filter((t) => t.parent_id === id && t.own_temperature !== 'alvó' && (t.temperature === 'forró' || t.wake_reason));
  const n = d.next;

  return (
    <div className="j-stack now">
      <div className="hello">
        {!compact && <JarvisCore size={96} />}
        <div><Label c="amber">Jarvis</Label><h1 className="greet">{greeting()}, Gyuri.</h1></div>
      </div>

      {n && (
        <Panel c="amber" className="j-next">
          <Label c="amber">Az egy következő lépés</Label>
          <h2>{n.title}</h2>
          <p>{n.why}{n.due ? ` · ${day(n.due)}` : ''}</p>
          {n.kind === 'task' && n.ref ? (
            <div className="j-row">
              <button className="j-btn j-btn--primary" onClick={() => task(n.ref!, 'done')}>Kész</button>
              <button className="j-btn" onClick={() => task(n.ref!, 'snooze', 1)}>Holnapra</button>
              <button className="j-btn" onClick={() => task(n.ref!, 'snooze', 7)}>Jövő hétre</button>
            </div>
          ) : n.id ? <div className="j-row"><a className="j-btn j-btn--primary" href={`#/szal/${n.id}`}>Megnyitom</a></div> : null}
        </Panel>
      )}

      {d.replies.filter((r) => !gone.has(r.event_id)).length > 0 && (
        <Panel c="coral"><Label c="coral">Válaszodra vár</Label>
          {d.replies.filter((r) => !gone.has(r.event_id)).map((r) => (
            <div className="j-slot" key={r.event_id}>
              <div><div className={`t ${r.days >= 3 ? 'late' : ''}`}>{r.days ? `${r.days} napja` : 'ma'}</div></div>
              <div><div><b>{r.from_name || r.from_email}</b> · {r.subject}</div>{r.question && <div className="w">{r.question}</div>}
                <div className="task-acts"><button className="j-btn" onClick={() => { hide(r.event_id, true); api(`/api/replies/${r.event_id}/done`, {}).then(load).catch((e) => { hide(r.event_id, false); setErr(e.message); }); }}>Nem kell válasz</button></div></div>
            </div>
          ))}
        </Panel>
      )}
      {d.decisions.length > 0 && (
        <>
          <a href="#/dontesek" className="label-link"><Label c="coral">Döntésre vár · {d.decisionCount} ›</Label></a>
          {d.decisions.filter((x) => !gone.has(x.event_id + x.thread_id)).map((x) => <PlacementCard key={x.event_id + x.thread_id} x={x} onAnswer={(a) => decide(x, a)} onMoved={() => { hide(x.event_id + x.thread_id, true); load(); }} />)}
        </>
      )}

      <div className="lenses">
        <a href="#/lencse/penz" className="j-panel j-lens j-c-mint lens-link">
          <div className="k">Fizetésig szabad</div>
          <div className="v j-num" style={{ color: d.money.state === 'over' ? 'var(--coral)' : undefined }}>{d.money.free != null ? ft(d.money.free) : '–'}</div>
          <div className="s">{d.money.perDay != null ? `napi ${ft(d.money.perDay)} · ` : ''}{d.money.daysToPayday} nap</div>
        </a>
        <a href="#/lencse/egyetem" className="j-panel j-lens j-c-violet lens-link">
          <div className="k">Egyetem</div>
          <div className="v j-num">{d.uni.next_exam ? day(d.uni.next_exam) : d.uni.next_class ? day(d.uni.next_class) : '–'}</div>
          <div className="s">{d.uni.next_exam ? `vizsga: ${d.uni.exam_subject}` : d.uni.next_class ? 'következő óra' : 'nincs kiírt vizsga'}{Number(d.uni.overdue) ? ` · ${d.uni.overdue} lejárt` : ''}</div>
        </a>
        <Panel c="cyan" className="j-lens">
          <div className="k">Szálak</div>
          <div className="v j-num">{hot.length} forró</div>
          <div className="s">{d.tasks.length} nyitott teendő · {d.decisionCount} döntés</div>
        </Panel>
      </div>

      {d.today.length > 0 && (
        <Panel c="mint"><Label c="mint">Ma</Label>
          {d.today.map((e) => (
            <div className="j-slot" key={e.id}>
              <div><div className="t">{time(e.at)}</div><div className="src">{e.kind === 'exam' ? 'vizsga' : e.kind === 'class' ? 'óra' : 'naptár'}</div></div>
              <div><div>{e.subject || e.text}</div>{e.location && <div className="w">{e.location}</div>}</div>
            </div>
          ))}
        </Panel>
      )}

      <Label c="cyan">Forró szálak</Label>
      {hot.map((t) => (
        <div key={t.id} className="thread-group">
          <a className="j-panel j-thread" style={tc(t.id)} href={`#/szal/${t.id}`}>
            <HeatRing heat={t.heat} id={t.id} />
            <div>
              <div className="kind">{t.focus ? 'fókusz · ' : ''}{t.wake_reason || t.temperature}</div>
              <h3>{t.name}</h3>
              {t.next_step && <div className="nx">{t.next_step}</div>}
            </div>
            <span className="when">{t.next_deadline ? day(t.next_deadline) : t.net ? ft(t.net) : ''}</span>
          </a>
          {hotKids(t.id).map((k) => (
            <a key={k.id} className="sub-thread" href={`#/szal/${k.id}`} style={tc(t.id)}>
              <span className="dot" /> {k.name}{k.wake_reason ? <em> · {k.wake_reason}</em> : ''}{k.next_deadline ? <em> · {day(k.next_deadline)}</em> : ''}
            </a>
          ))}
        </div>
      ))}

      {d.tasks.length > 0 && (
        <Panel c="coral"><Label c="coral">Teendők</Label>
          {d.tasks.map((t) => (
            <div className="j-slot task" key={t.ref}>
              <div className={`t ${t.overdue ? 'late' : ''}`}>{t.due ? day(t.due) : '–'}</div>
              <div className="task-body">
                <div>{t.title}</div>{t.thread && <div className="w">{t.thread}</div>}
                <div className="task-acts">
                  <button className="j-btn" onClick={() => task(t.ref, 'done')}>Kész</button>
                  <button className="j-btn" onClick={() => task(t.ref, 'snooze', 1)}>Holnap</button>
                </div>
              </div>
            </div>
          ))}
        </Panel>
      )}

      <Label c="cyan">Többi szál</Label>
      {top.filter((t) => t.temperature !== 'forró').map((t) => (
        <a key={t.id} className="j-panel j-thread j-thread--cold" style={tc(t.id)} href={`#/szal/${t.id}`}>
          <HeatRing heat={t.heat} id={t.id} />
          <div><div className="kind">{t.temperature}</div><h3>{t.name}</h3></div>
          <span className="when">{t.net ? ft(t.net) : ''}</span>
        </a>
      ))}
      {err && <p className="err">{err}</p>}
    </div>
  );
}
